import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Infinity as InfinityIcon, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Marquee from "../components/Marquee";
import Countdown from "../components/Countdown";
import ProductCard from "../components/ProductCard";
import DropSpotlight from "../components/DropSpotlight";
import { HOME, IMG, NEXT_DROP, getFeatured, byBrand } from "../mock/mock";

const Label = ({ children }) => (
  <span className="inline-block text-[11px] font-bold uppercase tracking-[0.24em] text-neon mb-3">
    {children}
  </span>
);

export default function Home() {
  const featured = getFeatured();
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const notify = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    try {
      await api.post("/subscribe", { email });
      toast("You're on the list", { description: "We'll alert you before the next drop closes." });
      setEmail("");
    } catch (err) {
      toast("Try again", { description: err?.response?.data?.detail || "Couldn't subscribe right now." });
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <main>
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden">
        <img src={IMG.hero} alt="Always-N-Anytime campaign" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/55 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        <div className="relative z-10 max-w-[1400px] mx-auto w-full px-4 md:px-8 pt-24">
          <div className="max-w-2xl rise-in">
            <span className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.2em] text-neon">
              <span className="w-2 h-2 rounded-full bg-neon animate-pulse" /> {HOME.badge}
            </span>
            <h1 className="font-display mt-4 leading-[0.85] text-6xl sm:text-7xl md:text-8xl xl:text-9xl">
              <span className="block text-white">{HOME.title1}</span>
              <span className="block text-neon neon-glow">{HOME.title2}</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/70 max-w-md">{HOME.subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/shop" className="bg-neon text-black font-bold uppercase tracking-[0.14em] text-sm px-8 py-4 rounded-full hover:brightness-95 transition">
                Shop the Drop
              </Link>
              <Link to="/designer" className="border border-white/40 text-white font-bold uppercase tracking-[0.14em] text-sm px-8 py-4 rounded-full hover:border-neon hover:text-neon transition">
                Design in 3D
              </Link>
            </div>
            <div className="mt-12">
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-neon mb-3">Next drop closes in</p>
              <Countdown target={NEXT_DROP} />
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      <DropSpotlight />

      {/* THE LINEUP */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-4xl md:text-6xl text-white">The Lineup</h2>
          <Link to="/shop" className="group flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-white/70 hover:text-neon transition">
            View all <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {featured.map((p) => (
            <ProductCard key={p.slug} product={p} />
          ))}
        </div>
      </section>

      {/* WHAT'S IN THE BOX */}
      <section className="bg-[#0c0c0d] border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Label>Every order</Label>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-none">What's in the box</h2>
            <p className="mt-6 text-white/65 leading-relaxed max-w-md">
              Every piece ships in a matte-black box stamped with the chained Always/Anytime mark.
              Peel the tape, lift the lid, meet your fit. Unboxing is part of the drop.
            </p>
            <p className="mt-4 text-sm uppercase tracking-[0.18em] text-neon">Print-on-demand · Ships worldwide</p>
          </div>
          <div className="relative grid grid-cols-2 gap-4">
            <div className="relative rounded-lg border border-white/10 overflow-hidden group">
              <img src={IMG.shesMineTee} alt="She's mine always, I'm hers anytime graffiti tee" className="object-cover aspect-[4/5] w-full img-zoom" />
              <span className="absolute bottom-3 left-3 text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded bg-neon text-black">The Tee</span>
            </div>
            <div className="relative rounded-lg border border-white/10 overflow-hidden group">
              <img src={IMG.shippingBox} alt="Always-N-Anytime matte-black shipping box" className="object-cover aspect-[4/5] w-full img-zoom" />
              <span className="absolute bottom-3 left-3 text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded bg-black/70 text-white border border-white/15">The Box</span>
            </div>
          </div>
        </div>
      </section>

      {/* THE INFINITY MARK */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
        <div className="relative order-2 md:order-1">
          <img src={IMG.coupleSet} alt="couple capsule" className="rounded-lg border border-white/10 object-cover w-full aspect-square" />
        </div>
        <div className="order-1 md:order-2">
          <Label>The Mark</Label>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-none flex items-center gap-3">
            The Infinity Mark <InfinityIcon className="text-neon" size={44} />
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed max-w-md">
            One symbol. No end. It stands for forever, loyalty, and refusing to be capped.
            Streetwear-first — with the Always / Anytime couple capsule as one loud chapter,
            not the whole story.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {HOME.values.map((v) => (
              <span key={v} className="text-xs font-semibold uppercase tracking-[0.14em] border border-neon/40 text-neon rounded-full px-4 py-2">
                {v}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* COUPLE CAPSULE */}
      <section className="relative bg-[#0c0c0d] border-y border-white/10">
        <div className="max-w-[1000px] mx-auto px-4 md:px-8 py-16 md:py-24 text-center">
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95]">
            She's mine always. <br />
            <span className="text-neon neon-glow">I'm hers anytime.</span>
          </h2>
          <p className="mt-4 text-sm uppercase tracking-[0.18em] text-white/50">Always (On) · (Get Off) Anytime</p>
          <p className="mt-5 text-white/65 max-w-xl mx-auto">
            The couple capsule — matching his/hers pairs. Pick the classic, or the cheeky one.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/product/shes-mine-always-graffiti-tee" className="bg-neon text-black font-bold uppercase tracking-[0.14em] text-sm px-7 py-3.5 rounded-full hover:brightness-95 transition">
              Shop the graffiti tee
            </Link>
            <Link to="/product/always-anytime-couple-set" className="border border-white/35 text-white font-bold uppercase tracking-[0.14em] text-sm px-7 py-3.5 rounded-full hover:border-neon hover:text-neon transition">
              Shop the classic pair
            </Link>
            <Link to="/product/always-on-getoff-anytime-set" className="border border-white/35 text-white font-bold uppercase tracking-[0.14em] text-sm px-7 py-3.5 rounded-full hover:border-neon hover:text-neon transition">
              Shop the cheeky pair
            </Link>
          </div>
        </div>
      </section>

      {/* SABOTAGE */}
      <BrandBlock
        kicker="A second house"
        title="Sabotage — Ozark Mountain Trash"
        blurb="Backwoods grit meets streetwear. Distressed prints, camo, and rust — proudly trashy."
        primary={{ to: "/shop?brand=sabotage", label: "Enter the trash pile" }}
        secondary={{ to: "/about-the-trash", label: "Meet the mascot · About the Trash" }}
        products={byBrand("sabotage", 4)}
        accent="trash"
      />

      {/* BOOTHILL */}
      <section className="bg-[#0c0c0d] border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-10 items-center mb-10">
            <div className="relative">
              <img src={IMG.boothillHome} alt="Boothill Bottoms" className="rounded-lg border border-white/10 object-cover w-full aspect-[16/10]" />
            </div>
            <div>
              <Label>A third house</Label>
              <h2 className="font-display text-4xl md:text-6xl text-white leading-none">Boothill Bottoms</h2>
              <p className="mt-3 text-sm uppercase tracking-[0.2em] text-neon">Survive · Adapt · Thrive</p>
              <p className="mt-5 text-white/65 max-w-md">
                Missouri boothill life in the Ozarks — a heart grown from the mountains for the
                ones who climbed back up. Rooted deep. Survive. Adapt. Thrive.
              </p>
              <div className="mt-7 flex flex-wrap gap-4">
                <Link to="/shop?brand=boothill-bottoms" className="bg-neon text-black font-bold uppercase tracking-[0.14em] text-sm px-7 py-3.5 rounded-full hover:brightness-95 transition">
                  Shop Boothill
                </Link>
                <Link to="/about-boothill" className="border border-white/35 text-white font-bold uppercase tracking-[0.14em] text-sm px-7 py-3.5 rounded-full hover:border-neon hover:text-neon transition">
                  The story
                </Link>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {byBrand("boothill-bottoms", 4).map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* STAND ON IT */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-10 items-center mb-10">
          <div>
            <Label>A fourth house</Label>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-none">Stand On It</h2>
            <p className="mt-5 text-white/65 max-w-md">
              Rooted in the Ozarks — a muddy boot stomp and one creed: STAND ON IT. Plant your
              feet and don't move.
            </p>
            <Link to="/shop?brand=stand-on-it" className="inline-block mt-7 bg-neon text-black font-bold uppercase tracking-[0.14em] text-sm px-7 py-3.5 rounded-full hover:brightness-95 transition">
              Shop Stand On It
            </Link>
          </div>
          <div className="relative order-first md:order-last">
            <img src={IMG.standHome} alt="Stand On It" className="rounded-lg border border-white/10 object-cover w-full aspect-[16/10]" />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {byBrand("stand-on-it", 4).map((p) => (
            <ProductCard key={p.slug} product={p} />
          ))}
        </div>
      </section>

      {/* HARDTIMEZ */}
      <BrandBlock
        kicker="A fifth house"
        title="Hardtimez"
        blurb="Built through the hard timez. Distressed cracked-diamond graphics for the ones who kept going \u2014 grit with a neon edge."
        primary={{ to: "/shop?brand=hardtimez", label: "Shop Hardtimez" }}
        secondary={{ to: "/shop", label: "See the full drop" }}
        products={byBrand("hardtimez", 4)}
      />

      {/* NEWSLETTER */}
      <section className="relative overflow-hidden bg-neon">
        <div className="max-w-[1000px] mx-auto px-4 md:px-8 py-16 md:py-24 text-center">
          <h2 className="font-display text-4xl md:text-7xl text-black leading-[0.9]">
            First to know.<br />First to grab.
          </h2>
          <p className="mt-4 text-black/70 font-medium">Drops sell fast. Get the alert before they're gone.</p>
          <form onSubmit={notify} className="mt-8 flex max-w-md mx-auto" data-testid="home-newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="home-newsletter-input"
              className="flex-1 min-w-0 bg-black/5 border border-black/25 rounded-l-full px-5 py-4 text-sm text-black placeholder:text-black/40 focus:outline-none focus:border-black"
            />
            <button
              type="submit"
              disabled={subscribing}
              data-testid="home-newsletter-submit"
              className="bg-black text-neon font-bold uppercase tracking-widest text-sm px-7 rounded-r-full hover:bg-[#111] transition disabled:opacity-60"
            >
              {subscribing ? "..." : "Notify Me"}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

function BrandBlock({ kicker, title, blurb, primary, secondary, products }) {
  return (
    <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
      <div className="mb-8">
        <Label>{kicker}</Label>
        <h2 className="font-display text-4xl md:text-6xl text-white leading-none">{title}</h2>
        <p className="mt-4 text-white/65 max-w-xl">{blurb}</p>
        <div className="mt-6 flex flex-wrap gap-4">
          <Link to={primary.to} className="bg-trash text-white font-bold uppercase tracking-[0.14em] text-sm px-7 py-3.5 rounded-full hover:brightness-110 transition">
            {primary.label}
          </Link>
          <Link to={secondary.to} className="border border-white/35 text-white font-bold uppercase tracking-[0.14em] text-sm px-7 py-3.5 rounded-full hover:border-white hover:text-white transition">
            {secondary.label}
          </Link>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {products.map((p) => (
          <ProductCard key={p.slug} product={p} />
        ))}
      </div>
    </section>
  );
}
